/**
 * Utilitaire simple pour la gestion des couleurs
 * Générique pour toute l'application
 */

/**
 * Détermine la couleur de texte la plus esthétique selon la couleur de fond
 * Version améliorée : plus permissive et moderne, garde le blanc plus longtemps
 * @param {string} backgroundColor - Couleur au format hexadécimal (avec ou sans #)
 * @return {string} - '#FFFFFF' pour blanc, '#2D3436' pour gris foncé, ou '#161616' pour noir
 */
export const getContrastTextColor = (backgroundColor) => {
  if (!backgroundColor || typeof backgroundColor !== 'string') {
    return '#FFFFFF';
  }

  // Retire le # si présent
  const color = backgroundColor.charAt(0) === '#' ? backgroundColor.substring(1, 7) : backgroundColor;

  // Validation basique
  if (color.length !== 6) {
    return '#FFFFFF';
  }

  try {
    // Convertit les valeurs hex en RGB
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);

    // ✅ Formule de luminance relative moderne (WCAG 2.1)
    const sR = r / 255;
    const sG = g / 255;
    const sB = b / 255;

    // Gamma correction
    const rLin = sR <= 0.03928 ? sR / 12.92 : Math.pow((sR + 0.055) / 1.055, 2.4);
    const gLin = sG <= 0.03928 ? sG / 12.92 : Math.pow((sG + 0.055) / 1.055, 2.4);
    const bLin = sB <= 0.03928 ? sB / 12.92 : Math.pow((sB + 0.055) / 1.055, 2.4);

    // Luminance relative
    const luminance = 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin;

    // ✅ Calculer la saturation pour les couleurs vives
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const saturation = max === 0 ? 0 : (max - min) / max;

    // ✅ Seuils ajustés pour un rendu esthétique optimal
    let threshold = 0.4; // Seuil de base (plus permissif que l'ancienne version)

    // ✅ Pour les couleurs saturées (comme l'or, orange, etc.), être plus permissif
    if (saturation > 0.3) {
      threshold = 0.5; // Encore plus permissif pour les couleurs vives
    }

    // ✅ Couleurs vraiment très claires : utiliser du noir
    if (luminance > 0.8) {
      return '#161616';
    }

    // ✅ Zone intermédiaire : gris foncé moderne (plus doux que le noir)
    if (luminance > threshold && luminance <= 0.8) {
      return '#2D3436'; // Gris anthracite moderne
    }

    // ✅ Couleurs sombres et moyennes : blanc
    return '#FFFFFF';
  } catch (error) {
    // Fallback en cas d'erreur
    return '#FFFFFF';
  }
};

/**
 * Version alternative simple avec seuil ajustable
 * Utile pour tester différents niveaux de permissivité
 * @param {string} backgroundColor - Couleur au format hexadécimal
 * @param {number} threshold - Seuil de luminosité (défaut: 200, plus haut = plus permissif)
 * @return {string} - Couleur de texte optimale
 */
export const getContrastTextColorSimple = (backgroundColor, threshold = 200) => {
  if (!backgroundColor || typeof backgroundColor !== 'string') {
    return '#FFFFFF';
  }

  const color = backgroundColor.charAt(0) === '#' ? backgroundColor.substring(1, 7) : backgroundColor;

  if (color.length !== 6) {
    return '#FFFFFF';
  }

  try {
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);

    // Formule de luminosité standard
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;

    // Seuil ajustable (200 par défaut = plus permissif que 158)
    return brightness > threshold ? '#2D3436' : '#FFFFFF';
  } catch (error) {
    return '#FFFFFF';
  }
};

// Palettes de couleurs génériques
export const COLOR_PALETTES = {
  vibrant: [
    '#e91e63', // Pink 500 - Rose vif
    '#ff5722', // Deep Orange 500 - Orange rouge
    '#9c27b0', // Purple 500 - Violet
    '#3f51b5', // Indigo 500 - Indigo
    '#009688', // Teal 500 - Teal
    '#795548', // Brown 500 - Marron
    '#607d8b', // Blue Grey 500 - Bleu gris
    '#ff9800', // Orange 500 - Orange
    '#8bc34a', // Light Green 500 - Vert clair
    '#f44336', // Red 500 - Rouge
    '#2196f3', // Blue 500 - Bleu
    '#4caf50', // Green 500 - Vert
  ],
  soft: [
    '#2196f3', // Blue 500 - Bleu principal
    '#f44336', // Red 500 - Rouge vif
    '#4caf50', // Green 500 - Vert nature
    '#ff9800', // Orange 500 - Orange énergique
    '#9e9e9e', // Grey 500 - Gris neutre
    '#673ab7', // Deep Purple 500 - Violet profond
    '#00bcd4', // Cyan 500 - Cyan frais
    '#8bc34a', // Light Green 500 - Vert pomme
    '#ffc107', // Amber 500 - Ambre doré
    '#e91e63', // Pink 500 - Rose moderne
    '#795548', // Brown 500 - Marron terre
    '#607d8b', // Blue Grey 500 - Bleu ardoise
  ],
};

/**
 * Assigne automatiquement une couleur à un élément
 * @param {string} paletteKey - Clé de la palette ('vibrant', 'soft')
 * @param {number} index - Index de l'élément dans la liste
 * @param {string|null} customColor - Couleur personnalisée (prioritaire)
 * @return {string} - Code couleur hexadécimal
 */
export function assignAutoColor(paletteKey, index, customColor = null) {
  // 1. Priorité : couleur personnalisée
  if (customColor && isValidHexColor(customColor)) {
    return customColor;
  }

  // 2. Utiliser la palette demandée
  const palette = COLOR_PALETTES[paletteKey];

  if (!palette || !Array.isArray(palette)) {
    console.warn(`Palette introuvable: ${paletteKey}`);
    return '#9e9e9e'; // Gris par défaut
  }

  // 3. Attribution cyclique
  return palette[index % palette.length];
}

/**
 * Valide qu'une chaîne est un code couleur hexadécimal valide
 * @param {string} color - Code couleur à valider
 * @return {boolean} - True si valide
 */
export function isValidHexColor(color) {
  if (typeof color !== 'string') return false;
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
}
